import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProfileListing } from '../../app/marketplace/components/ProfileCard';

export type QuickFilter = 'spotlight' | 'all' | 'top' | 'cheapest' | 'most-bought' | 'least-bought' | 'agents';
export type SortOption = 'highest-value' | 'lowest-value' | 'trending' | 'most-popular';
export type LocationScope = 'global' | 'home' | 'touring';

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasMore: boolean;
}

interface MarketplaceState {
  profiles: ProfileListing[];
  activeFilter: QuickFilter;
  activeSort: SortOption;
  userBalance: number;
  walletAddress: string | null;
  selectedProfile: ProfileListing | null;
  pagination: PaginationState;
  isLoading: boolean;
  error: string | null;
}

const initialState: MarketplaceState = {
  profiles: [],
  activeFilter: 'all',
  activeSort: 'highest-value',
  userBalance: 0,
  walletAddress: null,
  selectedProfile: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20,
    hasMore: false,
  },
  isLoading: false,
  error: null,
};

const marketplaceSlice = createSlice({
  name: 'marketplace',
  initialState,
  reducers: {
    setProfiles: (state, action: PayloadAction<ProfileListing[]>) => {
      state.profiles = action.payload;
      state.error = null;
    },

    appendProfiles: (state, action: PayloadAction<ProfileListing[]>) => {
      state.profiles = [...state.profiles, ...action.payload];
      state.error = null;
    },

    setActiveFilter: (state, action: PayloadAction<QuickFilter>) => {
      state.activeFilter = action.payload;
      state.profiles = []; // Clear profiles when filter changes
      state.pagination.currentPage = 1; // Reset to first page
    },

    setActiveSort: (state, action: PayloadAction<SortOption>) => {
      state.activeSort = action.payload;
      state.profiles = []; // Clear profiles when sort changes
      state.pagination.currentPage = 1; // Reset to first page
    },

    setUserBalance: (state, action: PayloadAction<number>) => {
      state.userBalance = action.payload;
    },

    setWalletAddress: (state, action: PayloadAction<string | null>) => {
      state.walletAddress = action.payload;
    },

    setSelectedProfile: (state, action: PayloadAction<ProfileListing | null>) => {
      state.selectedProfile = action.payload;
    },

    setPagination: (state, action: PayloadAction<Partial<PaginationState>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    nextPage: (state) => {
      if (state.pagination.hasMore) {
        state.pagination.currentPage += 1;
      }
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    resetMarketplace: (state) => {
      state.profiles = [];
      state.activeFilter = 'all';
      state.activeSort = 'highest-value';
      state.selectedProfile = null;
      state.pagination = initialState.pagination;
      state.isLoading = false;
      state.error = null;
    },

    // Optimistic update for profile spotlight
    updateProfileSpotlight: (
      state,
      action: PayloadAction<{ profileId: string; durationHours: number }>
    ) => {
      const { profileId, durationHours } = action.payload;
      const profileIndex = state.profiles.findIndex((p) => p.id === profileId);

      if (profileIndex !== -1) {
        const now = new Date();
        const endTime = new Date(now.getTime() + durationHours * 60 * 60 * 1000);

        // Update the profile to show it's spotlighted
        state.profiles[profileIndex] = {
          ...state.profiles[profileIndex],
          badges: {
            ...state.profiles[profileIndex].badges,
            spotlighted: true,
          },
          spotlight: {
            startTime: now.toISOString(),
            endTime: endTime.toISOString(),
          },
        };
      }
    },
  },
});

export const {
  setProfiles,
  appendProfiles,
  setActiveFilter,
  setActiveSort,
  setUserBalance,
  setWalletAddress,
  setSelectedProfile,
  setPagination,
  nextPage,
  setLoading,
  setError,
  clearError,
  resetMarketplace,
  updateProfileSpotlight,
} = marketplaceSlice.actions;

export default marketplaceSlice.reducer;
